import React from "react"
import Select from "react-select"
import { Button } from "../../ui/button"
import { installmentIncrementOptions } from "../../../objects/createServiceObjects"
import { SvgWarning, SvgTrash } from "../../icons"
import { timeTypes } from "../../../objects/createServiceObjects"

interface Props {
  formErrors: any
  isLocked: boolean
  serviceValues: any
  setServiceValues: any
}

const InstallmentDetails: React.FC<Props> = ({
  formErrors,
  isLocked,
  serviceValues,
  setServiceValues,
}) => {
  const installmentIncrements =
    serviceValues?.billing?.installmentIncrements ?? []

  const increaseInstallmentCount = (index: number | null) => {
    const billingIncrements = serviceValues.billing.installmentIncrements ?? []
    const packageIncrements = serviceValues.package.installmentIncrements ?? []

    const base =
      index !== null && billingIncrements[index]
        ? billingIncrements[index]
        : { time: 1, type: "months" }

    setServiceValues((prev: any) => ({
      ...prev,
      billing: {
        ...prev.billing,
        installmentIncrements: [...billingIncrements, base],
      },
      package: {
        ...prev.package,
        installmentIncrements: [...packageIncrements, base],
      },
    }))
  }

  const removeLastInstallment = () => {
    const next = serviceValues.billing.installmentIncrements.slice(0, -1)

    setServiceValues((prev: any) => ({
      ...prev,
      billing: {
        ...prev.billing,
        installmentIncrements: next,
      },
      package: {
        ...prev.package,
        installmentIncrements: next,
      },
    }))
  }

  const handleChangeInstallmentTime = (e: any, index: number) => {
    const updated = serviceValues.billing.installmentIncrements.map(
      (inc: any, i: number) => (i === index ? { ...inc, time: e.value } : inc)
    )

    setServiceValues((prev: any) => ({
      ...prev,
      billing: {
        ...prev.billing,
        installmentIncrements: updated,
      },
      package: {
        ...prev.package,
        installmentIncrements: updated,
      },
    }))
  }

  const handleChangeInstallmentType = (e: any, index: number) => {
    const updated = serviceValues.billing.installmentIncrements.map(
      (inc: any, i: number) => (i === index ? { ...inc, type: e.value } : inc)
    )

    setServiceValues((prev: any) => ({
      ...prev,
      billing: {
        ...prev.billing,
        installmentIncrements: updated,
      },
      package: {
        ...prev.package,
        installmentIncrements: updated,
      },
    }))
  }

  return (
    <>
      <div className="mb-4">
        <div className="w-full h-px bg-grayMist my-[32px]"></div>

        <p className="font-bold text-base mb-[10px]">Installment Details</p>
        <p className="font-bold text-base mb-[8px]">Percent due at booking</p>
        <div
          className={`${
            formErrors?.upFrontPercentage && "input-error"
          } default-input w-full`}
        >
          <input
            id="percentDue"
            onWheel={() => (document.activeElement as HTMLElement)?.blur()}
            disabled={isLocked}
            type="number"
            step="1"
            min="0"
            autoComplete="off"
            name="upFrontPercentage"
            placeholder="Percentage"
            value={serviceValues?.billing?.upFrontPercentage}
            onChange={(e: any) => {
              const value = e.target.value

              setServiceValues((prev: any) => ({
                ...prev,
                billing: {
                  ...prev.billing,
                  upFrontPercentage: value,
                },
                package: {
                  ...prev.package,
                  upFrontPercentage: value,
                },
              }))
            }}
          />
          <div className="overlay">Percentage</div>
          <div className="absolute top-[50%] -translate-y-[50%] right-[20px] text-graySlate peer-focus:text-black peer-hover:text-grayCharcoal">
            %
          </div>
        </div>
        {formErrors.upFrontPercentage && (
          <div className="field-error w-full">
            <SvgWarning />
            {formErrors.upFrontPercentage}
          </div>
        )}
      </div>

      {/* List of installments after first one */}

      <div className="mb-[24px]">
        {installmentIncrements.length > 0 ? (
          <h4 className="font-bold text-base mb-[8px]">Payment due</h4>
        ) : (
          <>
            <h4 className="font-bold text-base mb-[8px]">
              No installments planned
            </h4>
            <button
              className="btn-primary btn-secondary-nav mr-auto"
              onClick={() => increaseInstallmentCount(null)}
            >
              Add another installment
            </button>
          </>
        )}
        {installmentIncrements.map((input: number, index: number) => (
          <div key={index}>
            <div className="mb-[8px]">
              <div className="flex items-center justify-between">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_40px] gap-2 w-full min-w-0">
                  <div className="min-w-0">
                    <Select
                      className="basic-single w-full"
                      classNamePrefix="select"
                      options={installmentIncrementOptions}
                      menuPlacement="bottom"
                      styles={{
                        container: (base) => ({ ...base, minWidth: 0 }),
                        control: (base) => ({ ...base, minWidth: 0 }),
                      }}
                      value={installmentIncrementOptions.find(
                        (option) =>
                          option.value ===
                          serviceValues.billing.installmentIncrements[index]
                            .time
                      )}
                      onChange={(e) => handleChangeInstallmentTime(e, index)}
                    />
                  </div>

                  <div className="min-w-0">
                    <Select
                      className="basic-single w-full"
                      classNamePrefix="select"
                      options={timeTypes}
                      menuPlacement="bottom"
                      styles={{
                        container: (base) => ({ ...base, minWidth: 0 }),
                        control: (base) => ({ ...base, minWidth: 0 }),
                      }}
                      value={timeTypes.find(
                        (option) =>
                          option.value ===
                          serviceValues.billing.installmentIncrements[index]
                            .type
                      )}
                      onChange={(e) => handleChangeInstallmentType(e, index)}
                    />
                  </div>

                  <div className="flex justify-end">
                    {index === installmentIncrements.length - 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={removeLastInstallment}
                      >
                        <SvgTrash />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {formErrors.installmentIncrements && (
                <div className="field-error w-full">
                  <SvgWarning />
                  {formErrors.installmentIncrements}
                </div>
              )}
            </div>
            {index ===
              serviceValues.billing.installmentIncrements.length - 1 && (
              <Button
                variant="secondary"
                onClick={() => increaseInstallmentCount(index)}
              >
                Add another installment
              </Button>
            )}
          </div>
        ))}
      </div>
    </>
  )
}

export default InstallmentDetails
